/**
 * Browser compatibility utilities for Safari and other browsers
 * 
 * Includes Vimeo-inspired MediaRecorder configuration for optimal quality.
 * @see Base docs/Vimeo Recording Architecture Analysis.md
 */

import type { RecordingQuality } from "../context/RecorderContext";

/**
 * Resolution-based bitrates matching Vimeo's configuration.
 * Higher resolutions require proportionally higher bitrates to maintain quality.
 * 
 * Vimeo values: 4K=20Mbps, 2K=10Mbps, 1080p=5Mbps, 720p=2.5Mbps
 */
export const BITRATES_BY_QUALITY: Record<RecordingQuality, number> = {
  "2k":    10_000_000,  // 10 Mbps for 2K (2560x1440)
  "1080p":  5_000_000,  //  5 Mbps for Full HD (1920x1080)
  "720p":   2_500_000,  // 2.5 Mbps for HD (1280x720)
};

/**
 * Preferred codecs in order of preference for canvas recording.
 * H.264 (avc1) is preferred for hardware encoding support.
 * VP9/VP8 as fallbacks for browsers without H.264 support.
 */
const PREFERRED_CODECS = [
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',  // H.264 Baseline + AAC - best HW support
  'video/webm;codecs=vp9,opus',               // VP9 - good quality, software encoding
  'video/webm;codecs=vp8,opus',               // VP8 - wider support, lower quality
  'video/webm',                                // Browser default
];

/**
 * WebM-only codecs for separate stream recordings.
 * Files are saved with .webm extension and must contain WebM-compatible
 * codecs (VP8/VP9/AV1). H.264 in a .webm file causes FFmpeg remux failures.
 */
const WEBM_CODECS = [
  'video/webm;codecs=vp9,opus',               // VP9 - best quality for WebM
  'video/webm;codecs=vp8,opus',               // VP8 - wider support
  'video/webm',                                // Browser default
];

/**
 * Get the best supported mimeType for MediaRecorder.
 * @param webmOnly - If true, only returns WebM-compatible codecs (for separate stream recordings)
 */
export function getSupportedMimeType(webmOnly: boolean = false): string | undefined {
  if (typeof MediaRecorder === 'undefined') {
    return undefined;
  }
  
  const codecs = webmOnly ? WEBM_CODECS : PREFERRED_CODECS;
  
  for (const codec of codecs) {
    try {
      if (MediaRecorder.isTypeSupported(codec)) {
        return codec;
      }
    } catch {
      continue;
    }
  }
  return 'video/webm';
}

/**
 * Standard audio bitrate for all recordings.
 * 128 kbps provides good quality while keeping file size reasonable.
 */
export const AUDIO_BITRATE = 128_000; // 128 kbps

/**
 * Get MediaRecorder options optimized for the given quality.
 * Based on Vimeo's configuration for smooth, high-quality recordings.
 * 
 * @param quality - Recording quality preset (defaults to 1080p)
 * @returns MediaRecorderOptions with optimized bitrate and codec
 */
export function getMediaRecorderOptions(quality: RecordingQuality = "1080p"): MediaRecorderOptions {
  const mimeType = getSupportedMimeType();
  const videoBitsPerSecond = BITRATES_BY_QUALITY[quality];
  
  const options: MediaRecorderOptions = {
    videoBitsPerSecond,
    audioBitsPerSecond: AUDIO_BITRATE,
  };
  
  // Only set mimeType if we have a valid one
  if (mimeType) {
    options.mimeType = mimeType;
  }
  
  return options;
}

/**
 * Recommended timeslice for MediaRecorder.start().
 * Vimeo uses 10000ms. Smaller values cause more CPU interrupts.
 * We use 5000ms as a balance between responsiveness and performance.
 */
export const RECOMMENDED_TIMESLICE = 5000; // 5 seconds

/**
 * Vimeo-optimized timeslice (10 seconds).
 * Larger chunks reduce CPU interrupts and improve encoding efficiency.
 * Used when VIMEO_TIMESLICE_10S flag is enabled.
 */
export const VIMEO_TIMESLICE = 10000; // 10 seconds (matches Vimeo exactly)

/**
 * Shorter timeslice for separate stream recording (1 second).
 * When toggling streams rapidly (every 2-3 seconds), we need more frequent
 * data chunks to ensure short segments have data before being stopped.
 * This prevents missing frames in rapidly-toggled mid-recording segments.
 */
export const SEPARATE_STREAM_TIMESLICE = 1000; // 1 second for rapid toggle support

/**
 * Video keyframe interval duration in milliseconds.
 * Vimeo uses 6000ms (6 seconds) for optimal seeking and encoding.
 * This tells the encoder to insert a keyframe every 6 seconds.
 */
export const VIDEO_KEYFRAME_INTERVAL_DURATION = 6000; // 6 seconds

export interface BrowserInfo {
  isSafari: boolean;
  isChrome: boolean;
  isFirefox: boolean;
  isEdge: boolean;
  version: number;
  supportsOffscreenCanvas: boolean;
  supportsAudioWorklet: boolean;
  supportsWebCodecs: boolean;
  supportsMediaRecorder: boolean;
  supportsWorkerTypeModule: boolean;
}

/**
 * Detect browser type and capabilities
 */
export function detectBrowser(): BrowserInfo {
  const userAgent = navigator.userAgent.toLowerCase();
  
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isChrome = /chrome|chromium|crios/i.test(userAgent) && !isSafari;
  const isFirefox = /firefox|fxios/i.test(userAgent);
  const isEdge = /edg/i.test(userAgent);
  
  // Extract version number
  let version = 0;
  const versionMatch = userAgent.match(/(?:chrome|safari|firefox|edge|version)[\s\/:]([\d.]+)/i);
  if (versionMatch && versionMatch[1]) {
    version = parseFloat(versionMatch[1]);
  }

  // Check for specific API support
  const supportsOffscreenCanvas = typeof OffscreenCanvas !== 'undefined';
  const supportsAudioWorklet = typeof AudioWorkletNode !== 'undefined';
  const supportsWebCodecs = typeof VideoEncoder !== 'undefined' && typeof AudioEncoder !== 'undefined';
  const supportsMediaRecorder = typeof MediaRecorder !== 'undefined';
  const supportsWorkerTypeModule = supportsWorkerTypeModuleCheck();

  return {
    isSafari,
    isChrome,
    isFirefox,
    isEdge,
    version,
    supportsOffscreenCanvas,
    supportsAudioWorklet,
    supportsWebCodecs,
    supportsMediaRecorder,
    supportsWorkerTypeModule,
  };
}

/**
 * Check if browser supports worker type: 'module'
 */
function supportsWorkerTypeModuleCheck(): boolean {
  try {
    // Try to create a worker with type: 'module' to test support
    const testWorker = new Worker(
      URL.createObjectURL(new Blob([''], { type: 'application/javascript' })),
      { type: 'module' as any }
    );
    testWorker.terminate();
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Get browser compatibility warnings
 */
export function getCompatibilityWarnings(): string[] {
  const browser = detectBrowser();
  const warnings: string[] = [];

  if (browser.isSafari) {
    if (!browser.supportsWorkerTypeModule) {
      warnings.push('Safari may have limited recording functionality due to worker module support limitations.');
    }
    if (!browser.supportsWebCodecs) {
      warnings.push('Safari does not support WebCodecs API, which may affect video encoding quality.');
    }
    if (browser.version < 14) {
      warnings.push('Older Safari versions may have limited MediaRecorder support.');
    }
  }

  if (!browser.supportsMediaRecorder) {
    warnings.push('Your browser does not support MediaRecorder API. Recording may not work.');
  }

  if (!browser.supportsOffscreenCanvas) {
    warnings.push('OffscreenCanvas is not supported, which may affect performance.');
  }

  return warnings;
}

/**
 * Check if recording should work with current browser
 */
export function isRecordingSupported(): boolean {
  const browser = detectBrowser();
  
  // Basic requirements
  if (!browser.supportsMediaRecorder) {
    return false;
  }

  // Safari-specific checks
  if (browser.isSafari) {
    // Safari 14+ has better MediaRecorder support
    return browser.version >= 14;
  }

  return true;
}

/**
 * Get recommended fallback options for the current browser
 */
export function getFallbackOptions(): {
  useLegacyAudio: boolean;
  useCanvasCapture: boolean;
  useBasicWorker: boolean;
} {
  const browser = detectBrowser();
  
  return {
    // Safari has issues with AudioWorklet, use legacy audio processing
    useLegacyAudio: browser.isSafari || !browser.supportsAudioWorklet,
    
    // Use canvas capture instead of WebCodecs if not supported
    useCanvasCapture: !browser.supportsWebCodecs,
    
    // Use basic worker without module type for all browsers to avoid MIME type issues
    useBasicWorker: true,
  };
}